/**
 * Shape text manipulation utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import { isShapeType } from "../../../util/shapeTypeMapper";

/**
 * Create a function to add text to a shape
 * @param {Object} selectedObject - Currently selected object
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} setOffCanvasRectangles - Setter for off-canvas rectangles
 * @param {Function} setOffCanvasCircles - Setter for off-canvas circles
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} saveToHistory - Function to save to history
 * @param {React.RefObject} isElementBeingEditedRef - Ref to track if element is being edited
 * @param {Function} setPendingChanges - Setter for pending changes flag
 * @param {React.RefObject} pendingChangesRef - Ref to track pending changes (for synchronous updates)
 * @returns {Function} Function to add text to shape
 */
export const createOnAddText =
  (
    selectedObject,
    setRectangles,
    setCircles,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setSelectedObject,
    saveToHistory,
    isElementBeingEditedRef,
    setPendingChanges,
    pendingChangesRef
  ) =>
    (shapeId) => {
      const shapeType = selectedObject.type;

      if (!shapeId) return;

      // CRITICAL: Check if element is selected BEFORE updating state
      // This ensures we can defer the save properly
      const isEditing = isElementBeingEditedRef?.current ?? false;
      const hasSelectedObject = !!selectedObject; // Element is selected if selectedObject exists

      // CRITICAL: Update state using functional updates to ensure we're working with latest state
      // Log before and after to debug state updates

      if (isShapeType(shapeType, "Rect")) {
        setRectangles((prev) => {
          const updated = prev.map((rect) =>
            rect.id === shapeId ? { ...rect, text: "Sample Text" } : rect
          );
          const updatedRect = updated.find(r => r.id === shapeId);
          return updated;
        });
        // Also update off-canvas rectangles if the shape lives there
        setOffCanvasRectangles((prev) => {
          const updated = prev.map((rect) =>
            rect.id === shapeId ? { ...rect, text: "Sample Text" } : rect
          );
          const updatedRect = updated.find(r => r.id === shapeId);
          return updated;
        });
      } else if (isShapeType(shapeType, "Circle")) {
        setCircles((prev) => {
          const updated = prev.map((circle) =>
            circle.id === shapeId ? { ...circle, text: "Sample Text" } : circle
          );
          const updatedCircle = updated.find(c => c.id === shapeId);
          return updated;
        });
        // Also update off-canvas circles if the shape lives there
        setOffCanvasCircles((prev) => {
          const updated = prev.map((circle) =>
            circle.id === shapeId ? { ...circle, text: "Sample Text" } : circle
          );
          const updatedCircle = updated.find(c => c.id === shapeId);
          return updated;
        });
      }

      if (selectedObject?.id === shapeId) {
        setSelectedObject((prev) => {
          const updated = {
            ...prev,
            text: "Sample Text",
          };
          return updated;
        });
      }

      // CRITICAL: If element is selected (has selectedObject), ALWAYS set pendingChanges instead of saving immediately
      // This groups all property changes (text addition, width/height, stroke, text color) into one history entry
      // when element_edit_complete is called on unselection
      // Check both isEditing flag and if selectedObject exists (element is selected)
      // CRITICAL: Always defer when selectedObject exists - this ensures grouping works even if isEditing flag is not set

      if (isEditing || hasSelectedObject) {
        // Element is being edited/selected - mark as pending change, element_edit_complete will save everything together
        // CRITICAL: Update BOTH state and ref synchronously to ensure endElementEditing sees the change
        setPendingChanges(true);
        if (pendingChangesRef) {
          pendingChangesRef.current = true;
        } else {
          console.error(`❌ [ADD-TEXT] pendingChangesRef is undefined!`);
        }
        // DO NOT call saveToHistory here - it will be saved by element_edit_complete
        return; // Early return to prevent any saveToHistory call
      } else {
        // Element is not being edited/selected - save immediately
        setTimeout(() => saveToHistory("add_text_to_shape"), 50);
      }
    };

/**
 * Create a function to remove text from a shape
 * @param {Object} selectedObject - Currently selected object
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} setOffCanvasRectangles - Setter for off-canvas rectangles
 * @param {Function} setOffCanvasCircles - Setter for off-canvas circles
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} saveToHistory - Function to save to history
 * @param {React.RefObject} isElementBeingEditedRef - Ref to track if element is being edited
 * @param {Function} setPendingChanges - Setter for pending changes flag
 * @param {React.RefObject} pendingChangesRef - Ref to track pending changes (for synchronous updates)
 * @returns {Function} Function to remove text from shape
 */
export const createOnRemoveText =
  (
    selectedObject,
    setRectangles,
    setCircles,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setSelectedObject,
    saveToHistory,
    isElementBeingEditedRef,
    setPendingChanges,
    pendingChangesRef
  ) =>
    (shapeId) => {
      const shapeType = selectedObject.type;

      if (!shapeId) return;

      if (isShapeType(shapeType, "Rect")) {
        setRectangles((prev) =>
          prev.map((rect) =>
            rect.id === shapeId ? { ...rect, text: null } : rect
          )
        );
        // Also update off-canvas rectangles if the shape lives there
        setOffCanvasRectangles((prev) =>
          prev.map((rect) =>
            rect.id === shapeId ? { ...rect, text: null } : rect
          )
        );
      } else if (isShapeType(shapeType, "Circle")) {
        setCircles((prev) =>
          prev.map((circle) =>
            circle.id === shapeId ? { ...circle, text: null } : circle
          )
        );
        // Also update off-canvas circles if the shape lives there
        setOffCanvasCircles((prev) =>
          prev.map((circle) =>
            circle.id === shapeId ? { ...circle, text: null } : circle
          )
        );
      }

      if (selectedObject?.id === shapeId) {
        setSelectedObject((prev) => ({
          ...prev,
          text: null,
        }));
      }

      // CRITICAL: Check if element is selected BEFORE updating state
      const isEditing = isElementBeingEditedRef?.current ?? false;
      const hasSelectedObject = !!selectedObject; // Element is selected if selectedObject exists

      // CRITICAL: If element is selected (has selectedObject), ALWAYS set pendingChanges instead of saving immediately
      // This groups all property changes (text removal, width/height, stroke, etc.) into one history entry
      // when element_edit_complete is called on unselection
      // CRITICAL: Always defer when selectedObject exists - this ensures grouping works even if isEditing flag is not set
      if (isEditing || hasSelectedObject) {
        // Element is being edited/selected - mark as pending change, element_edit_complete will save everything together
        // CRITICAL: Update BOTH state and ref synchronously to ensure endElementEditing sees the change
        setPendingChanges(true);
        if (pendingChangesRef) {
          pendingChangesRef.current = true;
        }
        // DO NOT call saveToHistory here - it will be saved by element_edit_complete
        return; // Early return to prevent any saveToHistory call
      } else {
        // Element is not being edited/selected - save immediately
        setTimeout(() => saveToHistory("remove_text_from_shape"), 50);
      }
    };
